export const GAME_CONFIG = {
  baseClickPower: 1,
  baseCost: 10,
  costMultiplier: 1.15,
  tickRate: 100,
  saveInterval: 5000,
  xpPerLevel: 1000,
  godMode: true,
};

export const EXPERIMENTS = {
  culture_growth: {
    id: 'culture_growth',
    name: 'Cultivo em Placa de Petri',
    icon: '🧫',
    type: 'match3',
    difficulty: 'Fácil',
    duration: 180,
    description: 'Aprenda os fundamentos do cultivo de micro-organismos',
    requirements: { knowledge: 0 },
    rewards: { knowledge: 50, funding: 50, experience: 100 },
    unlocks: ['pcr_amplification'],
  },
  pcr_amplification: {
    id: 'pcr_amplification',
    name: 'PCR - Amplificação de DNA',
    icon: '🧬',
    type: 'timing',
    difficulty: 'Médio',
    duration: 240,
    description: 'Domine os ciclos de temperatura da PCR',
    requirements: { knowledge: 100, equipment: ['thermocycler'] },
    rewards: { knowledge: 150, funding: 200, experience: 300 },
    unlocks: ['dna_sequencing'],
  },
  dna_sequencing: {
    id: 'dna_sequencing',
    name: 'Sequenciamento de DNA',
    icon: '🔬',
    type: 'puzzle',
    difficulty: 'Difícil',
    duration: 300,
    description: 'Decodifique sequências genéticas',
    requirements: { knowledge: 500, equipment: ['sequencer'] },
    rewards: { knowledge: 300, funding: 500, experience: 600 },
    unlocks: ['fermentation'],
  },
  fermentation: {
    id: 'fermentation',
    name: 'Fermentação Industrial',
    icon: '⚗️',
    type: 'management',
    difficulty: 'Difícil',
    duration: 480,
    description: 'Gerencie um biorreator em tempo real',
    requirements: { knowledge: 300, equipment: ['bioreactor'] },
    rewards: { knowledge: 250, funding: 800, experience: 500 },
    unlocks: ['crispr_editing'],
  },
  crispr_editing: {
    id: 'crispr_editing',
    name: 'Edição Genética CRISPR',
    icon: '✂️',
    type: 'precision',
    difficulty: 'Expert',
    duration: 600,
    description: 'Realize edições precisas no genoma usando Cas9',
    requirements: { knowledge: 1000, equipment: ['sequencer', 'thermocycler'] },
    rewards: { knowledge: 1000, funding: 2000, experience: 1000 },
    unlocks: [],
  },
};

export const QUESTIONS = {
  culture_growth: [
    {
      id: 'q_culture_1',
      question: 'Qual é a principal fonte de carbono para a maioria dos micro-organismos em cultura?',
      options: ['Glicose', 'Oxigênio', 'Nitrogênio', 'Água'],
      correct: 0,
      explanation: 'Glicose é a fonte de carbono mais comum, fornecendo energia e esqueleto de carbono para síntese de moléculas.',
      points: 10,
    },
    {
      id: 'q_culture_2',
      question: 'O que acontece quando a temperatura de incubação está muito alta?',
      options: ['Crescimento mais rápido', 'Desnaturação de proteínas', 'Maior produção', 'Nada muda'],
      correct: 1,
      explanation: 'Temperaturas excessivas desnaturam proteínas e enzimas essenciais, matando as células.',
      points: 15,
    },
  ],
  pcr_amplification: [
    {
      id: 'q_pcr_1',
      question: 'Qual é a temperatura de desnaturação na PCR?',
      options: ['55°C', '72°C', '95°C', '37°C'],
      correct: 2,
      explanation: 'A 95°C, as ligações de hidrogênio entre as fitas de DNA se rompem, separando a dupla hélice.',
      points: 15,
    },
    {
      id: 'q_pcr_2',
      question: 'O que são primers na PCR?',
      options: ['Enzimas', 'Pequenas sequências de DNA', 'Proteínas', 'Nutrientes'],
      correct: 1,
      explanation: 'Primers são oligonucleotídeos que se anelam ao DNA alvo e iniciam a síntese pela DNA polimerase.',
      points: 20,
    },
    {
      id: 'q_pcr_3',
      question: 'Quantas cópias de DNA são geradas após 30 ciclos de PCR?',
      options: ['30', '900', 'Milhões', 'Mais de 1 bilhão'],
      correct: 3,
      explanation: 'A PCR tem amplificação exponencial: 2^30 = ~1 bilhão de cópias do fragmento alvo!',
      points: 25,
    },
  ],
  dna_sequencing: [
    {
      id: 'q_seq_1',
      question: 'Qual nucleotídeo é complementar à Adenina (A) no DNA?',
      options: ['Guanina (G)', 'Citosina (C)', 'Timina (T)', 'Uracila (U)'],
      correct: 2,
      explanation: 'Adenina sempre se pareia com Timina através de duas ligações de hidrogênio.',
      points: 20,
    },
    {
      id: 'q_seq_2',
      question: 'O que é o Método de Sanger?',
      options: ['Uma técnica de clonagem', 'Um método de sequenciamento de DNA', 'Um tipo de PCR', 'Uma forma de fermentação'],
      correct: 1,
      explanation: 'O método de Sanger utiliza didesoxinucleotídeos para terminar a cadeia de DNA em posições específicas, permitindo determinar a sequência.',
      points: 30,
    },
  ],
  fermentation: [
    {
      id: 'q_ferm_1',
      question: 'Qual o papel da agitação em um biorreator?',
      options: ['Apenas misturar', 'Oxigenação e homogeneização', 'Resfriar o meio', 'Aumentar a pressão'],
      correct: 1,
      explanation: 'A agitação garante que o oxigênio e os nutrientes sejam distribuídos uniformemente para todas as células.',
      points: 25,
    },
    {
      id: 'q_ferm_2',
      question: 'Por que o controle de pH é crítico na fermentação?',
      options: ['Para mudar a cor', 'Para economizar base/ácido', 'Enzimas têm pH ótimo de atuação', 'Não é importante'],
      correct: 2,
      explanation: 'O metabolismo celular depende de enzimas que funcionam apenas em faixas específicas de pH.',
      points: 25,
    },
  ],
  crispr_editing: [
    {
      id: 'q_crispr_1',
      question: 'O que guia a proteína Cas9 até o alvo?',
      options: ['DNA polimerase', 'RNA guia (gRNA)', 'Um primer', 'Uma proteína sinalizadora'],
      correct: 1,
      explanation: 'O gRNA (RNA guia) contém uma sequência complementar ao alvo que direciona a Cas9 para o local exato do corte.',
      points: 50,
    },
    {
      id: 'q_crispr_2',
      question: 'O que é a sequência PAM?',
      options: ['Uma proteína', 'Um tipo de antibiótico', 'Um motivo adjacente ao protoespaçador', 'Um erro de sequenciamento'],
      correct: 2,
      explanation: 'A PAM (Protospacer Adjacent Motif) é uma curta sequência de DNA (ex: NGG) necessária para que a Cas9 reconheça e corte o alvo.',
      points: 50,
    },
  ],
};

export const EQUIPMENT_SHOP = {
  microscope: { name: 'Microscópio Óptico', cost: 300, icon: '🔬', description: 'Observar células em detalhe' },
  incubator: { name: 'Incubadora', cost: 500, icon: '🌡️', description: 'Controle preciso de temperatura' },
  thermocycler: { name: 'Termociclador PCR', cost: 1000, icon: '🧬', description: 'Necessário para PCR' },
  bioreactor: { name: 'Biorreator', cost: 2500, icon: '⚗️', description: 'Fermentação em larga escala' },
  sequencer: { name: 'Sequenciador NGS', cost: 5000, icon: '💻', description: 'Sequenciamento de DNA' },
};
